import merge from 'lodash/merge';
import {useCallback, useEffect, useRef, useState} from 'react';
import {useGetTopMenuHidden} from '../../../common/shared/hooks/useGetTopMenuHidden';
import {isWindowScrollDisabled} from '../../../common/shared/utils/isWindowScrollDisabled.function';
import {twMerge} from '../../../common/twMerge';
import {MOBILE_HEADER_HEIGHT_PX} from '../../../header/constants';
import {PC_HEADER_HEIGHT} from '../../organisms/PcHeader/constants';
import {Sticky} from '../Sticky/Sticky';
import {StickyLocation} from '../Sticky/StickyLocation.enum';
import {TAB_POSITION_CONTROL_STICKY_Z_INDEX} from './constants';
import {TabPositionControlProps} from './TabPositionControl.interface';

export function TabPositionControl({
  depth,
  disabled,
  isMobile,
  isWebView,
  forceSticky,
  scrollTargetElement,
  className,
  children,
  style,
  ...props
}: TabPositionControlProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const scrollY = useRef(0);
  const [shouldHide, setShouldHide] = useState(false);
  const [translateYpx, setTranslateYpx] = useState(0);
  const [topPx, setTopPx] = useState(0);
  const {isTopMenuHidden} = useGetTopMenuHidden();

  const handleReverseScroll = useCallback(() => {
    // iOS의 경우 scrollY position 값이 음수인 경우가 존재합니다. (스크롤 최상단에서 스크롤 업 제스처를 할 경우)
    // 스크롤 최상단일 경우 반드시 상단 탭이 보여야하기 때문에 shouldHide 를 false 처리합니다
    // Scroll을 강제로 막아놓은 상황에도 상단탭을 항상 보여줍니다.
    const scrollTargetElementY = scrollTargetElement
      ? scrollTargetElement.scrollTop
      : window.scrollY;
    if (scrollTargetElementY <= 0 || isWindowScrollDisabled()) {
      if (shouldHide) {
        setShouldHide(false);
      }
      return;
    }
    if (scrollY.current < scrollTargetElementY) {
      // 정방향 스크롤인 경우
      setShouldHide(true);
    } else if (scrollY.current > scrollTargetElementY) {
      // 역방향 스크롤인 경우
      setShouldHide(false);
    }
    scrollY.current = scrollTargetElementY;
  }, [scrollTargetElement, shouldHide]);

  useEffect(() => {
    scrollY.current = scrollTargetElement
      ? scrollTargetElement.scrollTop
      : window.scrollY;
  }, [scrollTargetElement]);

  useEffect(() => {
    const _scrollTargetElement = scrollTargetElement || window;
    if (!disabled && depth > 1) {
      if (!forceSticky) {
        _scrollTargetElement.addEventListener('scroll', handleReverseScroll);
      }
      if (containerRef.current) {
        // Depth가 2인 경우에는 자신의 높이 만큼만 translate 합니다.
        let curTranslateYpx = containerRef.current.clientHeight;
        let curTopPx = isMobile
          ? isWebView
            ? 0
            : isTopMenuHidden
              ? 0
              : MOBILE_HEADER_HEIGHT_PX
          : PC_HEADER_HEIGHT;

        // Depth가 3이상인 경우, 자신의 높이 + 상위 탭 요소들의 높이들을 더한 만큼 translate 합니다.
        // isTopMenuHidden 상태에선 depth가 1인 gnb가 숨겨져있기 때문에 2번째 탭부터 계산합니다.
        // TODO: 추후 메인홈 외의 gnb가 없는 영역에서도 상단 메뉴가 접히도록 변경된다면 gnb 존재 여부를 계산에 고려해야합니다.
        for (let idx = depth - 1; idx >= (isTopMenuHidden ? 2 : 1); idx--) {
          const currentUpperDepthTab = document.getElementById(
            `tab-depth-${idx}`
          );
          if (currentUpperDepthTab) {
            curTopPx += currentUpperDepthTab.clientHeight;
            curTranslateYpx += currentUpperDepthTab.clientHeight;
          }
        }
        setTopPx(curTopPx);
        setTranslateYpx(curTranslateYpx);
      }
    } else {
      // disabled이거나 depth가 1인 경우에는 스크롤을 추적하지 않습니다.
      setTopPx(0);
      setTranslateYpx(0);
      setShouldHide(false);
      _scrollTargetElement.removeEventListener('scroll', handleReverseScroll);
    }
    return () => {
      _scrollTargetElement.removeEventListener('scroll', handleReverseScroll);
    };
  }, [
    depth,
    disabled,
    forceSticky,
    handleReverseScroll,
    isMobile,
    isWebView,
    scrollTargetElement,
    isTopMenuHidden,
  ]);

  return (
    <>
      {/* disabled이거나 depth가 1 미만인 예외상황에는 children을 그대로 보여줍니다. */}
      {(disabled || depth < 1) && children}
      {/* depth가 1인 경우에는 Sticky 헤더로써 동작합니다. */}
      {!disabled && depth === 1 && (
        <Sticky
          className="w-full"
          id={`tab-depth-${depth}`}
          // isTopMenuHidden 상태에선 헤더가 미노출되기 때문에 Top 위치로 설정합니다.
          location={
            isTopMenuHidden ? StickyLocation.Top : StickyLocation.BelowHeader
          }
          isMobile={isMobile}
          isWebView={isWebView}
          style={{
            zIndex: TAB_POSITION_CONTROL_STICKY_Z_INDEX,
          }}
        >
          {children}
        </Sticky>
      )}
      {!disabled && depth > 1 && (
        <div
          ref={containerRef}
          id={`tab-depth-${depth}`}
          className={twMerge(
            'sticky w-full overflow-hidden transition-all duration-300 will-change-transform',
            !forceSticky && 'translate-y-0',
            className
          )}
          style={merge(
            {
              top: topPx,
              zIndex: (6 - depth) * 10,
              transform: shouldHide
                ? `translateY(-${translateYpx}px)`
                : undefined,
            },
            style
          )}
          {...props}
        >
          {children}
        </div>
      )}
    </>
  );
}
